// Pure template-lifecycle decisions (no IO) — unit-testable; shared by the service + the builder UI.
// A 'retired' template (e.g. a pre-TSL strategy that embedded raw JavaScript) has a verified migrated
// SUCCESSOR. Retired records are kept for audit but must redirect on load + be hidden from discovery.

import type { StrategyTemplate } from '../types';

type Lc = Pick<StrategyTemplate, 'lifecycleStatus' | 'migratedToTemplateId'>;

/** A template is retired only when explicitly marked. Absent/unknown ⇒ active (back-compat). */
export function isRetired(t?: Lc | null): boolean {
  return !!t && t.lifecycleStatus === 'retired';
}

/** The verified successor id, if this template is retired AND points to one. Else null. */
export function successorId(t?: Lc | null): string | null {
  return isRetired(t) && t?.migratedToTemplateId ? t.migratedToTemplateId : null;
}

/** Should loading this template auto-redirect to a successor? Only when retired WITH a successor.
 *  (A retired template with NO successor must NOT redirect — it stays put and may still need migration.) */
export function shouldRedirectOnLoad(t?: Lc | null): boolean {
  return successorId(t) !== null;
}

/** Hide from normal discovery (Starter Templates / Templates / search / recents / clone-recs). Retired OR
 *  the legacy 'archived' tag both hide; rows remain fetchable with includeArchived/by direct id. */
export function isHiddenFromDiscovery(t: { lifecycleStatus?: string | null; tags?: string[] | null }): boolean {
  return t.lifecycleStatus === 'retired' || (t.tags || []).includes('archived');
}

export type BannerMode = 'none' | 'migrate' | 'retired-notice';

/** What the migration banner should render:
 *   • 'migrate'        — full actionable red warning: an ACTIVE editable record genuinely carrying
 *                        unsupported legacy code, with NO completed successor (user must act).
 *   • 'retired-notice' — compact neutral notice: a retired record (its successor exists / it's read-only) —
 *                        never the red "Migration available" panel.
 *   • 'none'           — nothing (native template, or no legacy code). */
export function bannerMode(args: { hasLegacyCode: boolean; retired: boolean; hasSuccessor: boolean }): BannerMode {
  if (args.retired || args.hasSuccessor) return args.hasLegacyCode ? 'retired-notice' : 'none';
  return args.hasLegacyCode ? 'migrate' : 'none';
}

/** Is the in-place "Update template" action allowed? Never for a retired record (no overwriting / republishing
 *  the retired legacy version). */
export function canUpdateInPlace(t?: Lc | null): boolean {
  return !isRetired(t);
}

/** A successor is safe to redirect TO only if it is itself ACTIVE and does not point back at the original —
 *  guards against self-reference, a successor that is ALSO retired, and a direct cycle. On any failure the
 *  caller must fail safe (render the original, no redirect) rather than loop or load a retired payload. */
export function isValidSuccessor(
  originalId: string,
  successor?: (Lc & { id?: string }) | null
): boolean {
  if (!successor || !successor.id) return false;       // missing successor record
  if (successor.id === originalId) return false;        // self-reference
  if (successor.lifecycleStatus === 'retired') return false; // successor is itself retired
  if (successor.migratedToTemplateId === originalId) return false; // direct cycle back to the original
  return true;
}
